import React, { useState, useEffect } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { Modal, Button } from 'react-bootstrap';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';

// Structural data only — titles/descriptions live in the lang files (L.upsell.options[key])
const upsellOptions = [
  {
    key: 'likes',
    monthlyPrice: 24,
    annualPrice: 12,
    iconClass: 'fa-solid fa-thumbs-up',
    color: '#635bff',
  },
  {
    key: 'views',
    monthlyPrice: 22,
    annualPrice: 11,
    iconClass: 'fa-solid fa-eye',
    color: '#1a7f4b',
  },
  {
    key: 'engagement',
    monthlyPrice: 17,
    annualPrice: 9,
    iconClass: 'fa-solid fa-chart-line',
    color: '#e8720c',
  }
];

const Upsell = ({ choice, step, profile, rank }) => {
  const navigate = useNavigate();
  const L = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [choices, setChoices] = useState([]);
  const [activeUpsells, setActiveUpsells] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingChanges, setPendingChanges] = useState(null);
  const location = useLocation();
  
  const isAnnual = searchParams.get('interval') === 'annual';
  const hasActivePlan = profile && rank?.status === 'active';
  const hasAnnualPlan = profile && rank?.planInterval === 'annual';

  useEffect(() => {
    if (hasActivePlan) {
      try {
        const upsells = JSON.parse(profile?.socials?.[profile?.selectedProfile]?.upsells || '[]');
        setActiveUpsells(Array.isArray(upsells) ? upsells : []);
        setChoices(Array.isArray(upsells) ? upsells : []);
      } catch (e) {
        setActiveUpsells([]);
        setChoices([]);
      }
    } else {
      const initialChoices = [];
      if (searchParams.get('likes') === 'true') initialChoices.push('likes');
      if (searchParams.get('views') === 'true') initialChoices.push('views');
      if (searchParams.get('engagement') === 'true') initialChoices.push('engagement');
      setChoices(initialChoices);
      if (typeof choice === "function") {
        choice(initialChoices);
      }
    }
  }, [profile, rank, hasActivePlan]);

  const handleCheckboxChange = (key) => {
    let updatedChoices;
    if (choices.includes(key)) {
      updatedChoices = choices.filter(k => k !== key);
    } else {
      updatedChoices = [...choices, key];
    }
    setChoices(updatedChoices);
    
    if (!hasActivePlan) {
      const newParams = new URLSearchParams(searchParams);
      if (updatedChoices.includes('likes')) {
        newParams.set('likes', 'true');
      } else {
        newParams.delete('likes');
      }
      if (updatedChoices.includes('views')) {
        newParams.set('views', 'true');
      } else {
        newParams.delete('views');
      }
      if (updatedChoices.includes('engagement')) {
        newParams.set('engagement', 'true');
      } else {
        newParams.delete('engagement');
      }
	  if (step == 3) {
		newParams.set('upsellStep', 'true');
	  } else {
		newParams.delete('upsellStep');
	  }
      setSearchParams(newParams);
      
      if (typeof choice === "function") {
        choice(updatedChoices);
      }
    }
  };

  // `profiles.upsells` is shared storage: POST /purchase/addons merges the boost
  // targeting extras (fast/usa/female) into the same array when they are bought
  // with balance on a single instant boost. Those are not subscription add-ons —
  // this screen neither sells nor manages them — so the banner lists only the
  // keys it actually offers, under their real titles rather than the raw keys.
  // `choices` deliberately keeps carrying them so saving here doesn't erase the
  // record of a boost extra the customer paid for.
  const activeAddons = upsellOptions.filter(o => activeUpsells.includes(o.key));

  const getChanges = () => {
    const added = choices.filter(c => !activeUpsells.includes(c));
    const removed = activeUpsells.filter(c => !choices.includes(c));
    return { added, removed };
  };

  const hasChanges = () => {
    const { added, removed } = getChanges();
    return added.length > 0 || removed.length > 0;
  };

  const calculatePriceChange = () => {
    const { added, removed } = getChanges();
    const useAnnualPricing = hasAnnualPlan || isAnnual;
    let change = 0;
    added.forEach(key => {
      const option = upsellOptions.find(o => o.key === key);
      if (option) change += useAnnualPricing ? option.annualPrice : option.monthlyPrice;
    });
    removed.forEach(key => {
      const option = upsellOptions.find(o => o.key === key);
      if (option) change -= useAnnualPricing ? option.annualPrice : option.monthlyPrice;
    });
    return change;
  };

  const handleSaveChanges = () => {
    const { added, removed } = getChanges();
    setPendingChanges({ added, removed, newUpsells: choices });
    setShowConfirmModal(true);
  };

  const confirmUpsellChanges = async () => {
    try {
      setLoading(true);
      const response = await axios.post("https://api.viralgpt.com/purchase/update-upsells", {
        upsells: pendingChanges.newUpsells
      }, {
        headers: {
          'Authorization': 'Bearer ' + localStorage.getItem('accessToken')
        }
      });
      if (response.data.message) {
        alert(response.data.message);
        window.location.reload();
      } else {
        alert(L.upsell.errorUpdating(response.data.error));
      }
    } catch (error) {
      alert(error.response?.data?.error || L.upsell.errorUpdatingFallback);
    } finally {
      setLoading(false);
      setShowConfirmModal(false);
    }
  };

  return (
    <div style={{ maxWidth: 520, margin: '0 auto', padding: '2rem 1rem 0' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
        <div className="stripe-icon-box stripe-icon-box--purple" style={{ margin: '0 auto 0.75rem' }}>
          <i className="fa-solid fa-rocket" style={{ color: '#635bff', fontSize: '1.1rem' }}></i>
        </div>
        <h2 className="stripe-heading stripe-heading--sm" style={{ marginBottom: 6 }}>
          {hasActivePlan ? L.upsell.manageAddons : L.upsell.boostYourGrowth}
        </h2>
        <p className="stripe-body stripe-body--sm stripe-text-muted" style={{ margin: 0 }}>
          {hasActivePlan
            ? L.upsell.manageSubheading
            : L.upsell.boostSubheading}
        </p>
      </div>

      {/* Active add-ons banner */}
      {hasActivePlan && activeAddons.length > 0 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '10px 14px',
          borderRadius: 10,
          background: '#d4f4e2',
          marginBottom: 16,
        }}>
          <i className="fa-solid fa-check-circle" style={{ color: '#1a7f4b', fontSize: '0.9rem' }}></i>
          <span className="stripe-body stripe-body--sm" style={{ fontWeight: 600, color: '#1a7f4b' }}>
            {L.upsell.activeList(activeAddons.map(o => L.upsell.options[o.key].title).join(', '))}
          </span>
        </div>
      )}

      {/* Upsell Cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {upsellOptions.map(option => {
          const isActive = activeUpsells.includes(option.key);
          const isSelected = choices.includes(option.key);
          const price = (hasAnnualPlan || isAnnual) ? option.annualPrice : option.monthlyPrice;

          return (
            <div
              key={option.key}
              onClick={() => handleCheckboxChange(option.key)}
              className="stripe-card--static"
              style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px 18px',
                borderRadius: 12,
                border: isSelected ? '2px solid #635bff' : '1px solid #e3e8ee',
                background: isSelected ? '#faf9ff' : '#fff',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                boxShadow: isSelected
                  ? '0 4px 12px rgba(99,91,255,0.12)'
                  : '0 2px 6px rgba(50,50,93,0.06)',
              }}
            >
              {/* Left: icon + text */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flex: 1, minWidth: 0 }}>
                {/* Icon */}
                <div style={{
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  background: isSelected ? `${option.color}15` : '#f6f9fc',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  transition: 'background 0.15s',
                }}>
                  <i className={option.iconClass} style={{ fontSize: '1rem', color: option.color }}></i>
                </div>

                {/* Title + description */}
                <div style={{ minWidth: 0, textAlign: 'left' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                    <span className="stripe-body stripe-body--sm stripe-text-dark" style={{ fontWeight: 600 }}>
                      {L.upsell.options[option.key].title}
                    </span>
                    {hasActivePlan && isActive && (
                      <span style={{
                        display: 'inline-block',
                        padding: '1px 8px',
                        borderRadius: 20,
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        letterSpacing: '0.04em',
                        textTransform: 'uppercase',
                        background: '#d4f4e2',
                        color: '#1a7f4b',
                      }}>
                        {L.upsell.activeBadge}
                      </span>
                    )}
                  </div>
                  <p className="stripe-body stripe-body--xs stripe-text-muted" style={{ margin: 0, lineHeight: 1.4 }}>
                    {L.upsell.options[option.key].description}
                  </p>
                </div>
              </div>

              {/* Right: price + checkbox */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0, marginLeft: 12 }}>
                <div style={{ textAlign: 'right' }}>
                  <span className="stripe-text-dark" style={{ fontSize: '1.1rem', fontWeight: 700 }}>
                    ${price}
                  </span>
                  <span className="stripe-text-muted" style={{ fontSize: '0.8rem', fontWeight: 500 }}>{L.upsell.perMo}</span>
                </div>
                {/* Custom checkbox */}
                <div style={{
                  width: 22,
                  height: 22,
                  borderRadius: 6,
                  border: isSelected ? '2px solid #635bff' : '2px solid #cbd5e1',
                  background: isSelected ? '#635bff' : '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.15s',
                  flexShrink: 0,
                }}>
                  {isSelected && (
                    <i className="fa-solid fa-check" style={{ color: '#fff', fontSize: '0.65rem' }}></i>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Price change summary + save button (active plan users) */}
      {hasActivePlan && hasChanges() && (
        <div style={{ marginTop: 20 }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '12px 16px',
            borderRadius: 10,
            background: calculatePriceChange() > 0 ? '#f0ebff' : '#f0fdf4',
            marginBottom: 12,
          }}>
            <i
              className={calculatePriceChange() > 0 ? 'fa-solid fa-arrow-up' : 'fa-solid fa-arrow-down'}
              style={{ color: calculatePriceChange() > 0 ? '#635bff' : '#1a7f4b', fontSize: '0.85rem' }}
            ></i>
            <span className="stripe-body stripe-body--sm" style={{ fontWeight: 600, color: calculatePriceChange() > 0 ? '#4338ca' : '#166534' }}>
              {L.upsell.billChange(hasAnnualPlan, calculatePriceChange() > 0, Math.abs(hasAnnualPlan ? calculatePriceChange() * 12 : calculatePriceChange()))}
            </span>
          </div>
          <button
            onClick={handleSaveChanges}
            disabled={loading}
            className="stripe-btn stripe-btn--primary w-100"
            style={{
              padding: '0.85rem',
              fontSize: '1rem',
              opacity: loading ? 0.6 : 1,
            }}
          >
            {loading ? (
              <><i className="fa-solid fa-spinner fa-spin" style={{ marginRight: 8 }}></i>{L.upsell.saving}</>
            ) : (
              <>{L.upsell.saveChanges}</>
            )}
          </button>
        </div>
      )}

      {/* Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton style={{ border: 'none', padding: '1.5rem 1.5rem 0' }}>
          <Modal.Title className="stripe-heading stripe-heading--xs" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className="fa-solid fa-arrows-rotate" style={{ color: '#635bff', fontSize: '1rem' }}></i>
            {L.upsell.confirmTitle}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '1.25rem 1.5rem' }}>
          {/* Changes summary card */}
          <div style={{
            background: '#f6f9fc',
            borderRadius: 12,
            padding: '1.25rem',
            border: '1px solid #e3e8ee',
          }}>
            <h4 className="stripe-body stripe-body--sm" style={{ fontWeight: 700, color: '#0a2540', marginBottom: 14 }}>
              {L.upsell.changesSummary}
            </h4>

            {pendingChanges?.added?.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                  <i className="fa-solid fa-plus-circle" style={{ color: '#1a7f4b', fontSize: '0.8rem' }}></i>
                  <span className="stripe-body stripe-body--sm" style={{ fontWeight: 600, color: '#1a7f4b' }}>{L.upsell.adding}</span>
                </div>
                {pendingChanges.added.map(key => {
                  const option = upsellOptions.find(o => o.key === key);
                  const price = hasAnnualPlan ? option?.annualPrice : option?.monthlyPrice;
                  return (
                    <div key={key} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 12px',
                      borderRadius: 8,
                      background: '#fff',
                      border: '1px solid #e3e8ee',
                      marginBottom: 6,
                    }}>
                      <span className="stripe-body stripe-body--sm stripe-text-dark" style={{ fontWeight: 500 }}>{L.upsell.options[key]?.title}</span>
                      <span className="stripe-body stripe-body--sm" style={{ fontWeight: 600, color: '#1a7f4b' }}>
                        ${hasAnnualPlan ? price * 12 : price}/{hasAnnualPlan ? L.upsell.yr : L.upsell.mo}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {pendingChanges?.removed?.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                  <i className="fa-solid fa-minus-circle" style={{ color: '#e74c3c', fontSize: '0.8rem' }}></i>
                  <span className="stripe-body stripe-body--sm" style={{ fontWeight: 600, color: '#e74c3c' }}>{L.upsell.removing}</span>
                </div>
                {pendingChanges.removed.map(key => {
                  const option = upsellOptions.find(o => o.key === key);
                  const price = hasAnnualPlan ? option?.annualPrice : option?.monthlyPrice;
                  return (
                    <div key={key} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 12px',
                      borderRadius: 8,
                      background: '#fff',
                      border: '1px solid #e3e8ee',
                      marginBottom: 6,
                    }}>
                      <span className="stripe-body stripe-body--sm stripe-text-dark" style={{ fontWeight: 500 }}>{L.upsell.options[key]?.title}</span>
                      <span className="stripe-body stripe-body--sm" style={{ fontWeight: 600, color: '#e74c3c' }}>
                        -${hasAnnualPlan ? price * 12 : price}/{hasAnnualPlan ? L.upsell.yr : L.upsell.mo}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Total change */}
            <div style={{ height: 1, background: '#e3e8ee', margin: '12px 0' }}></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="stripe-body stripe-body--sm" style={{ fontWeight: 700, color: '#0a2540' }}>
                {L.upsell.periodChange(hasAnnualPlan)}
              </span>
              <span style={{
                fontSize: '1.05rem',
                fontWeight: 700,
                color: calculatePriceChange() >= 0 ? '#0a2540' : '#1a7f4b',
              }}>
                {calculatePriceChange() >= 0 ? '+' : ''}${hasAnnualPlan ? calculatePriceChange() * 12 : calculatePriceChange()}/{hasAnnualPlan ? L.upsell.yr : L.upsell.mo}
              </span>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ border: 'none', padding: '0 1.5rem 1.5rem', gap: 8 }}>
          <Button
            variant="light"
            onClick={() => setShowConfirmModal(false)}
            style={{
              borderRadius: 10,
              padding: '0.6rem 1.25rem',
              fontWeight: 600,
              fontSize: '0.9rem',
              border: '1px solid #e3e8ee',
            }}
          >
            {L.upsell.cancel}
          </Button>
          <button
            onClick={confirmUpsellChanges}
            disabled={loading}
            className="stripe-btn stripe-btn--primary"
            style={{
              padding: '0.6rem 1.5rem',
              fontSize: '0.9rem',
              opacity: loading ? 0.6 : 1,
            }}
          >
            {loading ? (
              <><i className="fa-solid fa-spinner fa-spin" style={{ marginRight: 8 }}></i>{L.upsell.processing}</>
            ) : (
              <>{L.upsell.confirmChanges}</>
            )}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Upsell;
